/**
 * Minerve, mascotte officielle de Follaw : le dessin.
 *
 * EXTRAIT, PAS REDESSINE. Chaque forme, chaque nombre et chaque animation vient de la methode
 * `owl()` (et `propEl()`) du script de la charte « Minerve-Systeme-mascotte-v3.html » du
 * 18/10/2026. La structure est gardee ligne pour ligne pour que la comparaison reste lisible ;
 * le test de fidelite execute le script de la charte et verifie que l'arbre SVG produit ici est
 * identique, noeud par noeud. Un nombre retape a la main fait tomber ce test.
 *
 * Seules differences avec la charte, toutes sans effet sur le dessin :
 *  - `s()` remplace `React.createElement` et ecrit les attributs SVG en kebab-case
 *    (`strokeWidth` -> `stroke-width`), ce que React faisait de lui-meme ;
 *  - le suivi du curseur et le corps en degrade (reserves au hero et aux interdits de la
 *    charte) ne sont pas portes : le produit ne s'en sert pas.
 *
 * Nom de fichier : `minerve-dessin.ts` et non `minerve.ts`, qui se confondrait avec
 * `Minerve.ts` sur un disque insensible a la casse (Windows, macOS).
 */
import { h, type VNode } from 'vue'

/** Les couleurs de Minerve (section 02). Aucune autre couleur sur son corps. */
export const C = { dark: '#1B0F28', em: '#652470', cel: '#3F99D4', pink: '#FFD2FC' } as const

export type Oeil = 'open' | 'big' | 'half' | 'closed' | 'wide' | 'stern' | 'spiral' | 'down'

/** Les onze expressions de la section 03, plus « pudique » (yeux fermes pendant la saisie du code). */
export const EX = {
  neutre: ['open', 'open'], veille: ['open', 'open'], trouve: ['big', 'big'], lecture: ['half', 'half'],
  dort: ['closed', 'closed'], perplexe: ['big', 'half'], clin: ['open', 'closed'], surpris: ['wide', 'wide'],
  severe: ['stern', 'stern'], etourdi: ['spiral', 'spiral'], triste: ['down', 'down'], pudique: ['closed', 'closed'],
} as const satisfies Record<string, readonly [Oeil, Oeil]>
export type Expression = keyof typeof EX

const TUFT: Partial<Record<Expression, number>> = { trouve: 10, surpris: 12, severe: -12, triste: 28 }

export type Mouvement =
  | 'breath' | 'blink' | 'seats' | 'alerte' | 'hop' | 'wave' | 'nod' | 'shake' | 'startle' | 'fly'
  | 'flyaway' | 'tilt' | 'orbit' | 'read' | 'scan' | 'flicker'

const WRAP: Partial<Record<Mouvement, [string, number, string, string]>> = {
  startle: ['mvStartle', 2, 'ease-out', '50% 100%'], flyaway: ['mvFlyAway', 4, 'ease-in-out', '50% 50%'],
  fly: ['mvFloat', 2.4, 'ease-in-out', '50% 50%'], hop: ['mvHop', 1.6, 'ease-out', '50% 100%'],
  nod: ['mvNod', 2, 'ease-in-out', '50% 100%'], shake: ['mvShake', 2, 'ease-in-out', '50% 100%'],
  tilt: ['mvTilt', 3.6, 'ease-in-out', '50% 100%'],
}

/** Les douze actions du Studio (section 05), par leur nom de code (`action="..."`). */
export const ACTIONS = {
  repos: ['breath', 'blink'], scrutin: ['seats', 'breath'], alerte: ['alerte', 'hop'], salut: ['wave', 'blink'],
  oui: ['nod'], non: ['shake'], sursaut: ['startle'], vol: ['fly'], envol: ['flyaway'], ecoute: ['tilt', 'blink'],
  orbite: ['orbit'], lecture: ['read'],
} as const satisfies Record<string, readonly Mouvement[]>
export type Action = keyof typeof ACTIONS

/** Accessoires : les six du Studio, plus la pile (erreur 594) et le cadeau (anniversaire). */
export type Accessoire = 'loupe' | 'doc' | 'lock' | 'clock' | 'wifi' | 'barrier' | 'pile' | 'gift'

/** Niveau de detail (section 16) : complete >= 96 px, sans cocarde de 48 a 95 px, glyphe < 48 px. */
export type Detail = 'full' | 'mid' | 'glyph'
export const detailPourTaille = (taille: number): Detail => (taille >= 96 ? 'full' : taille >= 48 ? 'mid' : 'glyph')

export interface OptionsMinerve {
  size?: number
  expr?: Expression
  detail?: Detail
  anim?: readonly Mouvement[]
  body?: string
  prop?: Accessoire | null
  key?: string | number
}

type Attrs = Record<string, unknown>
type Enfant = VNode | null | undefined | false | Enfant[]

const kebab = (k: string) => (k === 'viewBox' ? k : k.replace(/[A-Z]/g, (m) => '-' + m.toLowerCase()))
/** `React.createElement` pour du SVG, en Vue : memes arguments, attributs en kebab-case. */
export function s(tag: string, attrs: Attrs | null, ...enfants: Enfant[]): VNode {
  const p: Attrs = {}
  for (const [k, v] of Object.entries(attrs || {})) {
    if (v === undefined) continue
    p[k === 'key' || k === 'style' ? k : kebab(k)] = v
  }
  const kids = (enfants.flat(Infinity as 1) as unknown[]).filter((e): e is VNode => e != null && e !== false)
  return h(tag, p, kids)
}

const SPIRAL = (cx: number) => 'M' + cx + ' 88 a2 2 0 0 1 0 4 a4 4 0 0 1 0 -8 a6 6 0 0 1 0 12 a8 8 0 0 1 0 -16 a10 10 0 0 1 0 20'

type Anim = (n: string, d: number, ease?: string | null, delay?: number) => string
type Style = Record<string, string | number | undefined>

let UID = 0

/** `A()` de la charte, vitesse 1 : l'animation CSS, ou 'none' quand elle est coupee. */
export const anim = (on: boolean): Anim => (n, d, ease, delay) =>
  on ? n + ' ' + d.toFixed(2) + 's ' + (ease || 'ease-in-out') + ' ' + (delay || 0).toFixed(2) + 's infinite' : 'none'

function propEl(prop: Accessoire | null | undefined, gid: string, has: (a: Mouvement) => boolean, A: Anim): VNode | null {
  if (!prop) return null
  const vb = (origin: string, animation: string): Style => ({ transformBox: 'view-box', transformOrigin: origin, animation })
  if (prop === 'loupe') return s('g', { key: 'pr', style: has('scan') ? { transformBox: 'fill-box', transformOrigin: 'center', animation: A('mvScan', 3) } : undefined },
    s('circle', { cx: 150, cy: 136, r: 18, fill: '#fff', fillOpacity: 0.45, stroke: C.dark, strokeWidth: 6 }),
    s('path', { d: 'M163 149 L180 166', stroke: C.dark, strokeWidth: 9, strokeLinecap: 'round' }))
  if (prop === 'doc') return s('g', { key: 'pr', transform: 'rotate(-6 100 212)' },
    s('rect', { x: 76, y: 194, width: 48, height: 36, rx: 3, fill: '#fff', stroke: C.dark, strokeWidth: 3 }),
    s('path', { d: 'M84 204 h32 M84 212 h32 M84 220 h20', stroke: C.cel, strokeWidth: 3, strokeLinecap: 'round' }))
  if (prop === 'lock') return s('g', { key: 'pr' },
    s('path', { d: 'M158 144 v-9 a10 10 0 0 1 20 0 v9', stroke: C.dark, strokeWidth: 5, fill: 'none' }),
    s('rect', { x: 151, y: 142, width: 34, height: 28, rx: 5, fill: C.dark }),
    s('circle', { cx: 168, cy: 153, r: 4, fill: C.cel }), s('rect', { x: 166.5, y: 154, width: 3, height: 8, rx: 1.5, fill: C.cel }))
  if (prop === 'clock') return s('g', { key: 'pr' },
    s('circle', { cx: 180, cy: 40, r: 18, fill: '#fff', stroke: C.dark, strokeWidth: 4 }),
    s('path', { d: 'M180 40 V30', stroke: C.dark, strokeWidth: 3.5, strokeLinecap: 'round' }),
    s('path', { d: 'M180 40 H192', stroke: C.cel, strokeWidth: 3.5, strokeLinecap: 'round', style: vb('180px 40px', A('mvSpin', 3, 'linear')) }),
    s('circle', { cx: 180, cy: 40, r: 2.5, fill: C.dark }))
  if (prop === 'wifi') return s('g', { key: 'pr', style: { animation: A('mvTwinkle', 2.4) } },
    [24, 15, 6].map((q, i) => s('path', { key: i, d: 'M' + (178 - q * 0.707) + ' ' + (58 - q * 0.707) + ' A' + q + ' ' + q + ' 0 0 1 ' + (178 + q * 0.707) + ' ' + (58 - q * 0.707), stroke: C.dark, strokeOpacity: 0.4, strokeWidth: 5, fill: 'none', strokeLinecap: 'round' })),
    s('circle', { cx: 178, cy: 58, r: 3.5, fill: C.dark, fillOpacity: 0.4 }),
    s('path', { d: 'M160 28 L196 62', stroke: C.em, strokeWidth: 5, strokeLinecap: 'round' }))
  if (prop === 'barrier') {
    const cid = gid + 'b', segs: VNode[] = []
    for (let i = 0; i < 10; i++) { const x = 30 + i * 16; segs.push(s('polygon', { key: i, points: x + ',168 ' + (x + 16) + ',168 ' + (x + 8) + ',186 ' + (x - 8) + ',186', fill: i % 2 ? C.pink : C.em })) }
    return s('g', { key: 'pr' },
      s('rect', { x: 46, y: 184, width: 7, height: 16, fill: C.dark }), s('rect', { x: 147, y: 184, width: 7, height: 16, fill: C.dark }),
      s('defs', null, s('clipPath', { id: cid }, s('rect', { x: 36, y: 168, width: 128, height: 18, rx: 3 }))),
      s('g', { clipPath: 'url(#' + cid + ')' }, segs),
      s('rect', { x: 36, y: 168, width: 128, height: 18, rx: 3, fill: 'none', stroke: C.dark, strokeWidth: 2.5 }))
  }
  if (prop === 'pile') return s('g', { key: 'pr' },
    [0, 1, 2, 3].map((i) => s('rect', { key: i, x: 150 + (i % 2) * 3, y: 176 - i * 9, width: 40, height: 8, rx: 2, fill: i % 2 ? C.pink : '#fff', stroke: C.dark, strokeWidth: 2.5 })),
    s('g', { style: { transformBox: 'fill-box', transformOrigin: 'center', animation: A('mvFloat', 2.2) } },
      s('rect', { x: 160, y: 108, width: 26, height: 32, rx: 2, fill: '#fff', stroke: C.dark, strokeWidth: 2.5, transform: 'rotate(14 173 124)' }),
      s('path', { d: 'M166 118 h14 M166 125 h14 M166 132 h8', stroke: C.cel, strokeWidth: 2.5, strokeLinecap: 'round', transform: 'rotate(14 173 124)' })),
    s('g', { style: { transformBox: 'fill-box', transformOrigin: 'center', animation: A('mvFloat', 2.6, null, 0.6) } },
      s('rect', { x: 6, y: 118, width: 24, height: 30, rx: 2, fill: '#fff', stroke: C.dark, strokeWidth: 2.5, transform: 'rotate(-16 18 133)' })))
  if (prop === 'gift') return s('g', { key: 'pr' },
    s('rect', { x: 70, y: 150, width: 60, height: 42, rx: 4, fill: '#fff', stroke: C.dark, strokeWidth: 3 }),
    s('path', { d: 'M100 150 V192 M70 168 H130', stroke: C.em, strokeWidth: 6 }),
    s('circle', { cx: 91, cy: 146, r: 7, fill: C.cel }), s('circle', { cx: 109, cy: 146, r: 7, fill: C.cel }), s('circle', { cx: 100, cy: 150, r: 4.5, fill: C.em }))
  return null
}

/**
 * Le dessin de Minerve : `owl()` de la charte. `on` vaut faux quand les animations sont coupees
 * (preference « reduire les animations », ou rendu fige) : la charte rend alors un dessin immobile.
 */
export function dessinerMinerve(o: OptionsMinerve, on: boolean): VNode {
  const size = o.size || 160, expr: Expression = o.expr || 'neutre', detail: Detail = o.detail || 'full', anims = o.anim || [], body = o.body || C.em
  const A = anim(on)
  const has = (a: Mouvement) => on && anims.indexOf(a) >= 0
  const fb = (origin: string, animation: string, extra?: Style): Style => Object.assign({ transformBox: 'fill-box', transformOrigin: origin, animation }, extra)
  const vb = (origin: string, animation: string): Style => ({ transformBox: 'view-box', transformOrigin: origin, animation })
  const E = EX[expr] || EX.neutre
  const look = expr === 'veille' && on
  const gid = 'mvg' + (UID++)
  const eye = (cx: number, k: Oeil, side: number): VNode[] => {
    const els: VNode[] = [s('circle', { key: 'r', cx, cy: 90, r: 24, fill: C.cel })]
    if (k === 'closed') { els.push(s('path', { key: 'c', d: 'M' + (cx - 14) + ' 90 Q' + cx + ' 102 ' + (cx + 14) + ' 90', stroke: C.dark, strokeWidth: 5, fill: 'none', strokeLinecap: 'round' })); return els }
    if (k === 'half') {
      els.push(s('path', { key: 'w', d: 'M' + (cx - 17) + ' 91 A17 17 0 0 0 ' + (cx + 17) + ' 91 Z', fill: '#fff' }),
        s('g', { key: 'p', style: has('read') ? fb('center', A('mvRead', 2.2)) : undefined }, s('circle', { cx, cy: 99, r: 6.5, fill: C.dark })),
        s('path', { key: 'l', d: 'M' + (cx - 19) + ' 91 H' + (cx + 19), stroke: C.dark, strokeWidth: 4, strokeLinecap: 'round' }))
      return els
    }
    if (k === 'spiral') {
      els.push(s('circle', { key: 'w', cx, cy: 90, r: 17, fill: '#fff' }),
        s('path', { key: 's', d: SPIRAL(cx), stroke: C.dark, strokeWidth: 3, fill: 'none', strokeLinecap: 'round', style: vb(cx + 'px 89px', A('mvSpin', 1.4, 'linear', side > 0 ? 0.2 : 0)) }))
      return els
    }
    const big = k === 'big', wide = k === 'wide', orbit = has('orbit')
    const wr = wide ? 20 : big ? 19 : 17, pr = wide ? 6 : big ? 11 : 8
    const py = orbit ? 82 : k === 'down' ? 97 : 90, r0 = orbit ? 6 : pr
    const pst = orbit ? vb(cx + 'px 90px', A('mvSpin', 1.1, 'linear')) : look ? fb('center', A('mvLook', 4)) : undefined
    const pupil = s('g', { key: 'pg', style: pst }, s('circle', { cx, cy: py, r: r0, fill: C.dark }), s('circle', { cx: cx + r0 * 0.35, cy: py - r0 * 0.35, r: Math.max(2, r0 * 0.32), fill: '#fff' }))
    const inner: VNode[] = [s('circle', { key: 'w', cx, cy: 90, r: wr, fill: '#fff' }), pupil]
    if (k === 'stern' || k === 'down') {
      const cid = gid + 'e' + cx, yi = k === 'stern' ? 86 : 74, yo = k === 'stern' ? 74 : 86
      const yL = side < 0 ? yo : yi, yR = side < 0 ? yi : yo
      inner.push(s('defs', { key: 'd' }, s('clipPath', { id: cid }, s('circle', { cx, cy: 90, r: wr }))),
        s('g', { key: 'lid', clipPath: 'url(#' + cid + ')' },
          s('polygon', { points: (cx - 22) + ',60 ' + (cx + 22) + ',60 ' + (cx + 22) + ',' + yR + ' ' + (cx - 22) + ',' + yL, fill: C.cel }),
          s('path', { d: 'M' + (cx - 22) + ' ' + yL + ' L' + (cx + 22) + ' ' + yR, stroke: C.dark, strokeWidth: 4 })))
    }
    els.push(s('g', { key: 'bl', style: has('blink') ? fb('center', A('mvBlink', 4.8)) : undefined }, inner))
    return els
  }
  const tuft = (dir: number) => {
    const pts = dir < 0 ? '46,66 56,20 84,50' : '154,66 144,20 116,50'
    const origin = dir < 0 ? '85% 95%' : '15% 95%'
    let st: Style | undefined
    if (has('alerte') || has('startle')) st = fb(origin, A(dir < 0 ? 'mvTuftL' : 'mvTuftR', has('alerte') ? 1.8 : 2))
    else if (TUFT[expr] != null) st = fb(origin, 'none', { transform: 'rotate(' + (dir * (TUFT[expr] as number)) + 'deg)' })
    return s('polygon', { key: 't' + dir, points: pts, fill: body, style: st })
  }
  const flap = has('fly') || has('flyaway')
  const wingL = s('path', { key: 'wl', d: 'M44 96 Q18 146 62 180 Q46 140 60 100 Z', fill: C.dark, style: flap ? fb('90% 4%', A('mvFlapL', 0.5)) : undefined })
  const wingR = s('path', { key: 'wr', d: 'M156 96 Q182 146 138 180 Q154 140 140 100 Z', fill: C.dark, style: flap ? fb('10% 4%', A('mvFlapR', 0.5)) : has('wave') ? fb('10% 4%', A('mvWave', 2.6)) : undefined })
  const R = detail === 'full' ? [32, 22, 12] : [30, 20, 10], sw = detail === 'full' ? 5 : 6
  const cols = [C.em, C.cel, C.em], dash = ['7 4', '6 4', undefined]
  const seatSt = (i: number): Style | undefined => has('seats') ? { animation: A('mvSeat', 1.8, null, i * 0.25) }
    : has('flicker') ? { animation: A('mvFlicker', 3, 'linear', i * 0.3) }
    : expr === 'dort' ? { opacity: 0.4 } : expr === 'etourdi' ? { opacity: 0.25 } : undefined
  const arcs = detail === 'glyph' ? null : R.map((r, i) => s('path', { key: 'a' + i, d: 'M' + (100 - r) + ' 180 A' + r + ' ' + r + ' 0 0 1 ' + (100 + r) + ' 180', stroke: cols[i], strokeWidth: sw, fill: 'none', strokeDasharray: dash[i], style: seatSt(i) }))
  const coc = detail === 'full' ? s('g', { key: 'coc', style: has('alerte') ? fb('center', A('mvPulse', 1.8)) : (expr === 'etourdi' ? { opacity: 0.35 } : undefined) },
    s('circle', { cx: 100, cy: 178, r: 7, fill: C.cel }), s('circle', { cx: 100, cy: 178, r: 4.5, fill: '#fff' }), s('circle', { cx: 100, cy: 178, r: 2, fill: C.em })) : null
  let wrap: Style | undefined
  const wk = (Object.keys(WRAP) as Mouvement[]).find((k) => has(k))
  if (wk) { const w = WRAP[wk]!; wrap = fb(w[3], A(w[0], w[1], w[2])) }
  else if (expr === 'perplexe') wrap = fb('50% 100%', 'none', { transform: 'rotate(-7deg)' })
  const zz = expr === 'dort' && on ? [0, 1, 2].map((i) => s('circle', { key: 'z' + i, cx: 150, cy: 56, r: 3 + i, fill: C.cel, style: { animation: A('mvRise', 2.4, 'ease-out', i * 0.8), opacity: 0 } })) : null
  return s('svg', { key: o.key, width: size, height: size, viewBox: '0 0 200 200', style: { overflow: 'visible', display: 'block' } },
    s('g', { style: wrap },
      s('g', { style: fb('50% 100%', has('breath') ? A('mvBreath', 3.2) : 'none') },
        tuft(-1), tuft(1), wingL, wingR,
        s('ellipse', { cx: 100, cy: 115, rx: 60, ry: 72, fill: body }),
        detail === 'glyph' ? null : s('ellipse', { cx: 100, cy: 148, rx: 36, ry: 38, fill: C.pink }),
        arcs, coc, eye(76, E[0], -1), eye(124, E[1], 1),
        s('polygon', { points: '100,110 92,120 100,130 108,120', fill: C.cel })),
      s('ellipse', { cx: 82, cy: 188, rx: 12, ry: 6, fill: C.cel }),
      s('ellipse', { cx: 118, cy: 188, rx: 12, ry: 6, fill: C.cel }),
      propEl(o.prop, gid, has, A)),
    zz)
}
